import { useContext, useEffect, useState } from "react";
import { Button, Col, Collapse, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import Resident from "../../Residents/Models/Resident";
import { abpnClient } from "../../Services/AbpnClient";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { TrainingAttestation } from "../Models/TrainingAttestation";
import { ProfileContext } from "../../Auth/ProfileProvider";

interface IProps {
    residency: Resident;
    onReturnedToProgram: (resident: Resident) => void;
}

export default function ReturnToProgramGrid(props: IProps) {
    const [isConfirmingReturn, setIsConfirmingReturn] = useState<boolean>(false);
    const [showReturnSection, setShowReturnSection] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isReturnToProgramDisabled, setIsReturnToProgramDisabled] = useState<boolean>(false);
    const [toolTipMessage, setToolTipMessage] = useState<string>("");
    const [existingAttestations, setExistingAttestations] = useState<boolean>(false);

    const authContext = useContext(AuthContext);
    const { user } = useContext(ProfileContext);

    const userTrainingProgram = user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);

    useEffect(() => {
        setIsReturnToProgramDisabled(false);
        setToolTipMessage("");
        returnToProgramDisabled();
        checkForExistingAttestations();
    }, [props.residency]);

    const confirmAndReturnToProgram = () => {
        let attestationsToRemove = props.residency.trainingAttestations?.filter(_ => _.trainingProgramId === user.selectedTrainingProgramId && _.isAttestedComplete == true);
        returnResidentToProgram(attestationsToRemove);
    }

    const returnResidentToProgram = async (attestations: TrainingAttestation[]) => {
        setIsSaving(true);
        let updatedTraining;

        if(attestations.length > 0){
            updatedTraining = await abpnClient.post<Resident>(`/api/training/clearTrainingAttestations?programTypeId=${userTrainingProgram.trainingProgramTypeId}`, attestations, authContext);
        }
        else{
            const url = `/api/training/transferResident?physicianId=${props.residency.physicianId}&trainingProgramId=${user.selectedTrainingProgramId}`;
            await abpnClient.post(url, {}, authContext);

            updatedTraining = props.residency;
            updatedTraining.currentTrainingProgramId = user.selectedTrainingProgramId;
        }
        props.onReturnedToProgram(updatedTraining);

        setIsSaving(false);
    }

    const displayReturnToProgramText = () => {
        if(existingAttestations)
            return (
                <div className="mb-2">
                    If you return this resident to your active roster, their final attestation of completed training will be deleted and they will move from your alumni roster back to your active roster.  
                    
                    After you return them to your active roster, in order to remove them from your active roster again, it will be necessary to A) 'Complete Final Attestation of Completed Residency Training', or B) 'Remove Resident from Program' (with incomplete training). For residents on your active roster, both of these options are located at the bottom of the resident details page.
                </div>
            );
        else
            return (
                <div className="mb-2">
                    If you return this resident to your active roster, they will move from your alumni roster back to your active roster.  
                    
                    After you return them to your active roster, in order to remove them from your active roster again, it will be necessary to A) 'Complete Final Attestation of Completed Residency Training', or B) 'Remove Resident from Program' (with incomplete training). For residents on your active roster, both of these options are located at the bottom of the resident details page.
                </div>
            );
    }

    const displayReturnToProgramModalText = () => {
        if(existingAttestations)
            return <strong>This resident's attestation of completed training will be deleted when you return them to your active roster.  Are your sure you want to return this resident to your active roster?</strong>;
        else
            return <strong>Are your sure you want to return this resident to your active roster?</strong>;
    }

    const checkForExistingAttestations = () => {
        let attestationsToRemove = props.residency.trainingAttestations?.filter(_ => _.trainingProgramId === user.selectedTrainingProgramId && _.isAttestedComplete == true);
        setExistingAttestations(attestationsToRemove.length > 0);
    }

    const returnToProgramDisabled = () => {
        if (props.residency.currentTrainingProgramId) {
            setToolTipMessage("This resident is currently on a different program's active roster. That program needs to remove the resident from their active roster in order for you to return the resident to yours.");
            setIsReturnToProgramDisabled(true);
            return;
        }

        if (props.residency.trainingAttestations?.find(_ => _.isAttestedComplete)) {
            if(user.userRole.isProgramCoordinator){
                setToolTipMessage("This resident has an attestation of completed residency training.  Only a PD can return a resident with an attestation of completed residency training to the active roster.");
                setIsReturnToProgramDisabled(true);
                return;
            }
        }

        setToolTipMessage("");
        setIsReturnToProgramDisabled(false);
    }

    const getReturnToProgramButton = () => {
        return <Button
                className="pc-button me-2"
                variant="primary"
                size="sm"
                disabled={isReturnToProgramDisabled}
                hidden={showReturnSection}
                onClick={() => setShowReturnSection(true)}>
                    Begin Return to Active Roster
                </Button>
    }

    return (
        <>
            <div className="d-flex justify-content-between">
                <h5 className="fw-bolder ml-2 mt-1 mb-4 text-primary">Return Alumni Resident to Active Roster</h5>
                <div>
                    {
                       isReturnToProgramDisabled ?
                        <OverlayTrigger placement="right" overlay={<Tooltip id="cannot_return_to_program" bsPrefix="pc-tooltip_warning">{toolTipMessage}</Tooltip>}>
                            <span className="d-inline-block">
                                {getReturnToProgramButton()}
                            </span>
                        </OverlayTrigger> : getReturnToProgramButton()
                    }
                </div>
            </div>
            {showReturnSection &&
            <Collapse in={showReturnSection}>
                <>
                <Row>
                    <Col>
                        <div className="mb-2">
                            {displayReturnToProgramText()}
                        </div>
                    </Col>
                </Row>
                <div className="mt-3 mb-3">
                    <strong>Return Dr. {props.residency.name.lastName} to your program's Active Roster?</strong>
                </div>
                <Button className="pc-button me-4" variant="primary" size="sm" onClick={() => setIsConfirmingReturn(true)}>Yes, Return</Button>
                <Button className="pc-button" variant="primary" size="sm" onClick={() => {setShowReturnSection(false)}}>No, Cancel</Button>
            </>
            </Collapse>

            }
            <Modal show={isConfirmingReturn} onHide={()=> setIsConfirmingReturn(false)}>
                <Modal.Body>
                    {displayReturnToProgramModalText()}
                </Modal.Body>
                <Modal.Footer>
                    {isSaving ? <AbpnSpinner /> :
                    <>
                        <Button variant="primary" className="me-4" onClick={() => confirmAndReturnToProgram()}>
                            Yes, Confirm
                        </Button>
                        <Button variant="outline-secondary" onClick={() => setIsConfirmingReturn(false)}>
                            No, Cancel
                        </Button>
                    </>
                    }
                </Modal.Footer>
            </Modal>
        </>
    );
}